import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { KrookiePlot } from "../services/reducers/master-plan-modification";
import { LandPlot } from "../interfaces/card";
import { LayersDataTypes } from "../types/layers-data-types";
import { Layer } from "../interfaces/layer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

export type PlotForSelfAndDuplicate = Pick<
  KrookiePlot,
  "newGeometry" | "objectId"
>;
export type PlotForIntersection = Pick<LandPlot, "geometry" | "objectId">;

export const hasDuplicatePoints = async (
  currentPlots: PlotForSelfAndDuplicate[],
): Promise<boolean> => {
  const allPoints = new Set<string>();

  for (const plot of currentPlots) {
    const polygon = plot.newGeometry;
    if (!polygon || !polygon.rings) continue;

    for (const ring of polygon.rings) {
      const seenInRing = new Set<string>();
      const points = ring.slice(0, -1);

      for (const [x, y] of points) {
        const key = `${x},${y}`;

        if (seenInRing.has(key)) {
          return true;
        }
        seenInRing.add(key);

        if (allPoints.has(key)) {
          return true;
        }
        allPoints.add(key);
      }
    }
  }

  return false;
};

export const hasSelfIntersections = async (
  currentPlots: PlotForSelfAndDuplicate[],
): Promise<boolean> => {
  for (const plot of currentPlots) {
    const polygon = plot.newGeometry;
    if (!polygon) continue;

    const isSimple = geometryEngine.isSimple(polygon);
    if (!isSimple) {
      return true;
    }
  }

  return false;
};

export const hasIntersections = async (
  currentPlots: PlotForSelfAndDuplicate[],
  allPlots: PlotForIntersection[],
  initialPlots?: LandPlot[],
): Promise<boolean> => {
  const initialObjectIds = new Set(initialPlots?.map((plot) => plot.objectId));

  for (const plot of currentPlots) {
    const newGeom = plot.newGeometry;
    if (!newGeom) continue;

    for (const existing of allPlots) {
      const existingGeom = existing.geometry;
      if (!existingGeom) continue;

      if (initialObjectIds.has(existing.objectId)) {
        continue;
      }

      const isIntersecting = geometryEngine.intersects(newGeom, existingGeom);
      const isTouching = geometryEngine.touches(newGeom, existingGeom);

      if (isIntersecting && !isTouching) {
        return true;
      }
    }
  }

  return false;
};

export const hasIntersectionsWithInitialPlot = async (
  currentPlots: PlotForSelfAndDuplicate[],
  initialPlots?: LandPlot[],
): Promise<boolean> => {
  if (!initialPlots || initialPlots.length === 0) return false;

  for (const plot of currentPlots) {
    const newGeom = plot.newGeometry;
    if (!newGeom) continue;

    for (const initialPlot of initialPlots) {
      const initialGeom = initialPlot.geometry;
      if (!initialGeom) continue;

      const isIntersecting = geometryEngine.intersects(newGeom, initialGeom);
      const isTouching = geometryEngine.touches(newGeom, initialGeom);

      if (isIntersecting && !isTouching) {
        return false;
      }
    }
  }

  return true;
};

export const isOutsideCityBoundary = async (
  currentPlots: PlotForSelfAndDuplicate[],
  layers: Layer[],
  cityId: number,
): Promise<boolean> => {
  const filteredCityLayers = layers.filter(
    (layer) => layer.dataType === LayersDataTypes.CityBoundary && !!layer.url,
  );

  const featureLayers = filteredCityLayers.map(
    (layer) =>
      new FeatureLayer({
        url: layer.url!,
        outFields: ["*"],
      }),
  );

  const plotGeometry = currentPlots?.[0]?.newGeometry;
  if (!plotGeometry) {
    return false;
  }

  for (const layer of featureLayers) {
    await layer.load();

    const query = layer.createQuery();
    query.where = `estateEn = ${cityId}`;
    query.returnGeometry = true;

    const result = await layer.queryFeatures(query);
    const features = result.features.map((feature) => feature.geometry);

    if (!features.length) continue;

    const mergedCityFeatures = geometryEngine.union(features);

    if (geometryEngine.contains(mergedCityFeatures, plotGeometry)) return false;
  }

  return true;
};

export const hasUnclosedRings = (
  currentPlots: PlotForSelfAndDuplicate[],
): boolean => {
  for (const plot of currentPlots) {
    const rings = plot.newGeometry?.rings;
    if (!rings) continue;

    for (const ring of rings) {
      if (ring.length < 4) {
        return true;
      }
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        return true;
      }
    }
  }
  return false;
};
